'use client';

import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createClient, forceSignOut } from '@/lib/supabase/client';

// Singleton Supabase client — never recreated
const supabase = createClient();

// Keys on globalThis so they survive Next.js hot-reload module re-evaluations
const SUB_KEY = '__supabase_auth_sub__';
const SETTER_KEY = '__supabase_auth_setters__';

type Setters = {
  setSession: (s: any) => void;
  setUser: (u: any) => void;
  setLoading: (l: boolean) => void;
};

function getSetters(): Setters | null {
  return (globalThis as any)[SETTER_KEY] ?? null;
}

function registerSetters(s: Setters) {
  (globalThis as any)[SETTER_KEY] = s;
}

function hasSubscription(): boolean {
  return !!(globalThis as any)[SUB_KEY];
}

/** Returns true if the error is an "Invalid Refresh Token: Already Used" or similar auth error */
function isInvalidRefreshTokenError(err: any): boolean {
  if (!err) return false;
  const msg: string = (err?.message ?? '').toLowerCase();
  return (
    msg.includes('invalid refresh token') ||
    msg.includes('already used') ||
    msg.includes('refresh_token_not_found') ||
    err?.code === 'refresh_token_not_found'
  );
}

function createSubscription() {
  const { data } = supabase.auth.onAuthStateChange((event, updatedSession) => {
    const setters = getSetters();
    if (!setters) return;

    if (event === 'SIGNED_OUT') {
      setters.setSession(null);
      setters.setUser(null);
      setters.setLoading(false);
      return;
    }

    // TOKEN_REFRESHED with null session means the refresh token was invalid/already used
    if (event === 'TOKEN_REFRESHED' && !updatedSession) {
      // Use forceSignOut to avoid Web Lock deadlock
      if (typeof window === 'undefined' || !(window as any).__sb_signing_out__) {
        if (typeof window !== 'undefined') (window as any).__sb_signing_out__ = true;
        forceSignOut();
        const s = getSetters();
        if (s) {
          s.setSession(null);
          s.setUser(null);
          s.setLoading(false);
        }
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return;
    }

    // INITIAL_SESSION with null simply means no user is logged in — this is
    // the normal state on the login/register pages. Do NOT redirect or call
    // forceSignOut here; doing so causes an infinite refresh loop on the
    // login page on some browsers.
    if (event === 'INITIAL_SESSION' && !updatedSession) {
      const s = getSetters();
      if (s) {
        s.setSession(null);
        s.setUser(null);
        s.setLoading(false);
      }
      return;
    }

    setters.setSession(updatedSession);
    setters.setUser(updatedSession?.user ?? null);
    setters.setLoading(false);
  });
  (globalThis as any)[SUB_KEY] = data.subscription;
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Suppress AbortError / "Lock broken" unhandled rejections — these are
    // internal Supabase Web Lock races and are handled by the auth state
    // change subscription. Letting them surface as unhandled rejections
    // causes downstream data fetches to fail unnecessarily.
    function suppressLockAbortErrors(event: PromiseRejectionEvent) {
      const reason = event?.reason;
      const msg: string = reason?.message ?? '';
      const name: string = reason?.name ?? '';

      // Suppress ALL AbortError / Web Lock races — these are internal Supabase
      // mechanisms and must never trigger a sign-out or redirect.
      // Check by error name (DOMException 'AbortError') as well as message content.
      const isAbortOrLockError =
        name === 'AbortError' ||
        reason instanceof DOMException ||
        msg.includes('AbortError') ||
        msg.includes('Lock broken') ||
        msg.toLowerCase().includes('lock broken') ||
        msg.toLowerCase().includes('steal') ||
        msg.toLowerCase().includes('web lock') ||
        msg.toLowerCase().includes('aborted') ||
        msg.toLowerCase().includes('lock manager');

      if (isAbortOrLockError) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return; // Do NOT call clearSessionAndRedirect for lock errors
      }

      // Only catch invalid refresh token errors surfacing as unhandled rejections
      // when they are NOT wrapped in an AbortError.
      if (isInvalidRefreshTokenError(reason)) {
        event.preventDefault();
        clearSessionAndRedirect();
      }
    }

    // Catch "Invalid Refresh Token: Already Used" errors that surface as
    // synchronous window errors from inside the Supabase Web Lock mechanism.
    function handleWindowError(event: ErrorEvent) {
      const msg: string = event?.message ?? '';
      if (
        msg.toLowerCase().includes('invalid refresh token') ||
        msg.toLowerCase().includes('already used') ||
        msg.toLowerCase().includes('refresh_token_not_found')
      ) {
        event.preventDefault();
        // Only redirect if not already signing out — prevents duplicate redirects
        if (typeof window !== 'undefined' && !(window as any).__sb_signing_out__) {
          clearSessionAndRedirect();
        }
      }
    }

    window.addEventListener('unhandledrejection', suppressLockAbortErrors);
    window.addEventListener('error', handleWindowError);

    // Always register the current component's setters so the singleton
    // subscription dispatches to the currently mounted instance.
    registerSetters({
      setSession: (s) => { if (mountedRef.current) setSession(s); },
      setUser: (u) => { if (mountedRef.current) setUser(u); },
      setLoading: (l) => { if (mountedRef.current) setLoading(l); },
    });

    // Helper: detect AbortError in all its forms
    function isAbortError(err: any): boolean {
      return (
        err?.name === 'AbortError' || err?.message?.includes('AbortError') ||
        err?.message?.includes('Lock broken') ||
        err?.cause?.name === 'AbortError'
      );
    }

    function clearSessionAndRedirect() {
      // Use global flag to prevent multiple simultaneous sign-out attempts
      // from different handlers (onAuthError, console.error intercept, etc.)
      if (typeof window !== 'undefined' && (window as any).__sb_signing_out__) return;
      if (typeof window !== 'undefined') (window as any).__sb_signing_out__ = true;

      // Use forceSignOut (no Web Lock) to avoid deadlock when the refresh
      // token is already used and the lock is held by the failing refresh.
      forceSignOut();
      if (mountedRef.current) {
        setSession(null);
        setUser(null);
        setLoading(false);
      }
      // Only redirect if not already on the login page — prevents refresh loops
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      } else if (typeof window !== 'undefined') {
        // Already on login page — reset flag immediately so future sign-ins work
        (window as any).__sb_signing_out__ = false;
      }
    }

    function retryGetSession() {
      setTimeout(() => {
        supabase.auth.getSession().then(({ data: { session: retrySession } }) => {
          if (!mountedRef.current) return;
          setSession(retrySession);
          setUser(retrySession?.user ?? null);
          setLoading(false);
        }).catch((retryErr) => {
          if (!mountedRef.current) return;
          // If still AbortError on retry, just stop loading — don't sign out
          if (isAbortError(retryErr)) {
            setLoading(false);
            return;
          }
          if (isInvalidRefreshTokenError(retryErr)) {
            clearSessionAndRedirect();
            return;
          }
          setSession(null);
          setUser(null);
          setLoading(false);
        });
      }, 200);
    }

    // Seed initial state from cached session (no network lock).
    supabase.auth.getSession().then(({ data: { session: initialSession }, error }) => {
      if (!mountedRef.current) return;
      if (error) {
        // AbortError means a Web Lock was stolen (e.g. another tab / hot-reload).
        // This is not an auth failure — just re-fetch once after a short delay.
        if (isAbortError(error)) {
          retryGetSession();
          return;
        }
        // Invalid/already-used refresh token — clear the stale session and redirect
        if (isInvalidRefreshTokenError(error)) {
          clearSessionAndRedirect();
          return;
        }
        // Other auth error — clear locally (use forceSignOut to avoid lock contention)
        forceSignOut();
        if (mountedRef.current) {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
        return;
      }

      // Use the session user directly — avoid calling getUser() here as it
      // competes for the same Web Lock that getSession() just released,
      // causing "Lock broken by another request with the 'steal' option".
      // The onAuthStateChange subscription will validate the token and
      // update state if the session turns out to be invalid.
      if (initialSession) {
        setSession(initialSession);
        setUser(initialSession.user ?? null);
        setLoading(false);
      } else {
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    }).catch((err) => {
      // getSession() itself can throw an AbortError as a real JS exception
      if (!mountedRef.current) return;
      if (isAbortError(err)) {
        retryGetSession();
        return;
      }
      if (isInvalidRefreshTokenError(err)) {
        clearSessionAndRedirect();
        return;
      }
      setSession(null);
      setUser(null);
      setLoading(false);
    });

    // Create the subscription exactly once for the lifetime of the app.
    if (!hasSubscription()) {
      createSubscription();
    }

    return () => {
      mountedRef.current = false;
      window.removeEventListener('unhandledrejection', suppressLockAbortErrors);
      window.removeEventListener('error', handleWindowError);
      // Do NOT unsubscribe — the subscription must live for the app lifetime.
    };
  }, []);

  // Email/Password Sign Up
  const signUp = useCallback(async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  }, []);

  // Email/Password Sign In
  const signIn = useCallback(async (email: string, password: string) => {
    // Reset the signing-out flag so auth state changes work after re-login
    if (typeof window !== 'undefined') {
      (window as any).__sb_signing_out__ = false;
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  }, []);

  // Sign Out
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  // Get Current User — read from cached state (no navigator lock)
  const getCurrentUser = useCallback(async () => {
    return user ?? null;
  }, [user]);

  // Check if Email is Verified
  const isEmailVerified = useCallback(() => {
    return user?.email_confirmed_at !== null;
  }, [user]);

  // Get User Profile from Database
  const getUserProfile = useCallback(async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  }, [user]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    supabase
  }), [user, session, loading, signUp, signIn, signOut, getCurrentUser, isEmailVerified, getUserProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
